import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, Download, Link2, List, Lock, Loader2, Play, X } from 'lucide-react';
import { UserProfile } from '../types';
import { getPreviewAuthHeaders } from '../lib/firebase';
import { GuideBlocks, parseGuideMarkdown, GUIDE_CSS, GUIDE_PRINT_CSS } from '../lib/guideMarkdown';

// =====================================================================
// Web study guide reader (2026-10-28). Opens at /guides/<paperId>/<n>, so
// any chapter link can be shared. Free chapters load for anyone; the server
// only sends other chapters to readers with access to the paper, and the
// page shows a sign up / subscribe card in their place. "Download PDF"
// opens a print layout in a new window and the browser saves it, so no
// files are stored on our side.
// =====================================================================

interface ChapterMeta { n: number; key: string; title: string; free: boolean; words: number; locked: boolean }
interface GuideMeta { paperId: string; title: string; subtitle: string; edition: string; updatedAt: string; chapters: ChapterMeta[]; signedIn: boolean; access: boolean }
interface ChapterData { n: number; title: string; markdown: string }

interface Props {
  paperId: string;
  initialChapter?: number;
  userProfile?: UserProfile;
  onChapterChange?: (n: number) => void;
  onOpenAuthModal?: (mode?: 'login' | 'register') => void;
  openPricingModal?: () => void;
  onStartTest?: (paperId: string) => void;
  onBack?: () => void;
}

const NAVY = '#0f1b4d';
const TEAL = '#0E9594';

const isGuest = (u?: UserProfile) => !u || !u.uid || u.uid === 'guest_user_1';

async function authHeaders(u?: UserProfile): Promise<Record<string, string>> {
  if (isGuest(u)) return {};
  try { return await getPreviewAuthHeaders(); } catch { return {}; }
}

const readKey = (paperId: string) => `pp-guide-read:${paperId}`;
function loadRead(paperId: string): number[] {
  try { return JSON.parse(localStorage.getItem(readKey(paperId)) || '[]'); } catch { return []; }
}
function saveRead(paperId: string, list: number[]) {
  try { localStorage.setItem(readKey(paperId), JSON.stringify(list)); } catch { /* private mode */ }
}

const minutes = (words: number) => Math.max(1, Math.round(words / 230));

export const WebGuideReader: React.FC<Props> = ({ paperId, initialChapter = 1, userProfile, onChapterChange, onOpenAuthModal, openPricingModal, onStartTest, onBack }) => {
  const [meta, setMeta] = useState<GuideMeta | null>(null);
  const [metaError, setMetaError] = useState('');
  const [n, setN] = useState(initialChapter);
  const [cache, setCache] = useState<Record<number, ChapterData>>({});
  const [locked, setLocked] = useState<Record<number, 'signin' | 'subscribe'>>({});
  const [loadingCh, setLoadingCh] = useState(false);
  const [chError, setChError] = useState('');
  const [tocOpen, setTocOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [read, setRead] = useState<number[]>(() => loadRead(paperId));
  const [copied, setCopied] = useState(false);
  const [printing, setPrinting] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);
  const uid = userProfile?.uid || '';

  // Meta (reloads when the reader signs in or out).
  useEffect(() => {
    let alive = true;
    setMeta(null); setMetaError(''); setCache({}); setLocked({});
    (async () => {
      try {
        const res = await fetch(`/api/guides/${encodeURIComponent(paperId)}`, { headers: await authHeaders(userProfile) });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.error || 'This guide could not be loaded.');
        if (alive) setMeta(body as GuideMeta);
      } catch (e: any) {
        if (alive) setMetaError(e?.message || 'This guide could not be loaded.');
      }
    })();
    return () => { alive = false; };
  }, [paperId, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = meta?.chapters.length || 0;
  const current = meta?.chapters.find((c) => c.n === n);

  useEffect(() => {
    if (meta && (n < 1 || n > total)) setN(1);
  }, [meta, n, total]);

  const fetchChapter = useCallback(async (k: number): Promise<ChapterData | 'signin' | 'subscribe' | null> => {
    const res = await fetch(`/api/guides/${encodeURIComponent(paperId)}/chapters/${k}`, { headers: await authHeaders(userProfile) });
    const body = await res.json().catch(() => ({}));
    if (res.status === 403 && body?.locked) return body.reason === 'subscribe' ? 'subscribe' : 'signin';
    if (!res.ok) throw new Error(body?.error || 'This chapter could not be loaded.');
    return body as ChapterData;
  }, [paperId, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Current chapter.
  useEffect(() => {
    if (!meta || !current) return;
    onChapterChange?.(n);
    topRef.current?.scrollIntoView({ block: 'start' });
    window.scrollTo({ top: 0 });
    if (cache[n] || locked[n]) return;
    if (current.locked) { setLocked((s) => ({ ...s, [n]: meta.signedIn ? 'subscribe' : 'signin' })); return; }
    let alive = true;
    setLoadingCh(true); setChError('');
    fetchChapter(n)
      .then((r) => {
        if (!alive) return;
        if (r === 'signin' || r === 'subscribe') setLocked((s) => ({ ...s, [n]: r }));
        else if (r) setCache((s) => ({ ...s, [n]: r }));
      })
      .catch((e) => alive && setChError(e?.message || 'This chapter could not be loaded.'))
      .finally(() => alive && setLoadingCh(false));
    return () => { alive = false; };
  }, [meta, n]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reading progress and "read" marks.
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      const p = max > 0 ? Math.min(1, el.scrollTop / max) : 1;
      setProgress(p);
      if (p > 0.92 && cache[n] && !read.includes(n)) {
        const next = [...read, n];
        setRead(next); saveRead(paperId, next);
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [n, cache, read, paperId]);

  const blocks = useMemo(() => (cache[n] ? parseGuideMarkdown(cache[n].markdown) : []), [cache, n]);

  const go = (k: number) => { if (k >= 1 && k <= total) { setN(k); setTocOpen(false); } };

  const shareUrl = `${window.location.origin}/guides/${encodeURIComponent(paperId)}/${n}`;
  const share = async () => {
    const title = meta ? `${meta.title}: ${current?.title || ''}` : 'Study guide';
    try {
      if ((navigator as any).share && window.matchMedia('(max-width: 768px)').matches) {
        await (navigator as any).share({ title, url: shareUrl });
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { /* user cancelled */ }
  };

  const downloadPdf = async () => {
    if (!meta) return;
    if (!meta.access) { if (meta.signedIn) openPricingModal?.(); else onOpenAuthModal?.('register'); return; }
    const win = window.open('', '_blank');
    if (!win) { alert('Please allow pop-ups for prepraxis.com to download the PDF.'); return; }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${meta.title.replace(/</g, '')}</title><style>${GUIDE_CSS}${GUIDE_PRINT_CSS}</style></head><body><div id="gp-root" style="max-width:7in;margin:0 auto;padding:24px"><p style="font:15px system-ui">Preparing your PDF...</p></div></body></html>`);
    win.document.close();
    setPrinting(true);
    try {
      const chapters: { meta: ChapterMeta; data: ChapterData | null }[] = [];
      for (const c of meta.chapters) {
        let data = cache[c.n] || null;
        if (!data) {
          const r = await fetchChapter(c.n);
          data = r && typeof r === 'object' ? r : null;
        }
        chapters.push({ meta: c, data });
      }
      const rootEl = win.document.getElementById('gp-root');
      if (!rootEl) return;
      const root = createRoot(rootEl);
      root.render(<PrintDocument meta={meta} chapters={chapters} />);
      setTimeout(() => { try { win.focus(); win.print(); } catch { /* closed */ } }, 900);
    } catch (e: any) {
      try { win.document.body.innerHTML = `<p style="font:15px system-ui;padding:24px">Sorry, the PDF could not be prepared: ${String(e?.message || e)}</p>`; } catch { /* closed */ }
    } finally {
      setPrinting(false);
    }
  };

  if (metaError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <BookOpen className="w-8 h-8 mx-auto text-slate-300" />
        <p className="mt-3 font-semibold text-slate-700">{metaError}</p>
        {onBack && <button onClick={onBack} className="mt-4 text-sm font-bold cursor-pointer" style={{ color: TEAL }}>Back to study guides</button>}
      </div>
    );
  }
  if (!meta) {
    return <div className="py-24 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const lockReason = locked[n];
  const toc = (
    <nav className="space-y-1">
      {meta.chapters.map((c) => {
        const active = c.n === n;
        return (
          <button
            key={c.n}
            onClick={() => go(c.n)}
            className={`w-full text-left flex items-start gap-2.5 rounded-lg px-3 py-2 text-[13.5px] transition cursor-pointer ${active ? 'bg-[#0f1b4d] text-white' : 'text-slate-700 hover:bg-slate-100'}`}
          >
            <span className={`shrink-0 w-6 text-right font-bold tabular-nums ${active ? 'text-teal-300' : 'text-slate-400'}`}>{c.n}</span>
            <span className="flex-1 leading-snug">{c.title}</span>
            {c.locked ? <Lock className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${active ? 'text-amber-300' : 'text-amber-500'}`} />
              : read.includes(c.n) ? <CheckCircle2 className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${active ? 'text-teal-300' : 'text-teal-600'}`} />
              : c.free && !meta.access ? <span className={`text-[10px] font-bold mt-0.5 ${active ? 'text-teal-200' : 'text-teal-700'}`}>FREE</span> : null}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div ref={topRef} className="relative">
      <style>{GUIDE_CSS}</style>
      <div className="fixed top-0 left-0 right-0 h-1 z-50 bg-transparent pointer-events-none">
        <div className="h-full transition-[width]" style={{ width: `${progress * 100}%`, background: TEAL }} />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="min-w-0">
            {onBack && (
              <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800 cursor-pointer mb-1">
                <ArrowLeft className="w-3.5 h-3.5" /> All study guides
              </button>
            )}
            <h1 className="text-xl sm:text-2xl font-extrabold leading-tight" style={{ color: NAVY }}>{meta.title}</h1>
            {meta.subtitle && <p className="text-sm text-slate-500">{meta.subtitle}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setTocOpen(true)} className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 cursor-pointer">
              <List className="w-3.5 h-3.5" /> Chapters
            </button>
            <button onClick={share} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:bg-slate-50 cursor-pointer">
              <Link2 className="w-3.5 h-3.5" /> {copied ? 'Link copied' : 'Share chapter'}
            </button>
            <button onClick={downloadPdf} disabled={printing} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white cursor-pointer disabled:opacity-60" style={{ background: NAVY }}>
              {meta.access ? <Download className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />} {printing ? 'Preparing...' : 'Download PDF'}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside className="hidden lg:block">
            <div className="sticky top-20 bg-white border border-slate-200 rounded-2xl p-3 max-h-[calc(100vh-7rem)] overflow-y-auto">
              <div className="px-3 pt-1 pb-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">Chapters</div>
              {toc}
              {!meta.access && (
                <div className="mt-3 mx-1 rounded-xl bg-teal-50 border border-teal-200 p-3 text-xs text-teal-900">
                  Chapter 1 is free. {meta.signedIn ? 'Subscribe' : 'Create a free account and subscribe'} to read every chapter and download the PDF.
                </div>
              )}
            </div>
          </aside>

          <main className="min-w-0">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm px-5 sm:px-10 py-8">
              <div className="flex items-center justify-between gap-3 text-xs font-bold uppercase tracking-wide" style={{ color: TEAL }}>
                <span>Chapter {n} of {total}</span>
                {current && current.words > 0 && <span className="text-slate-400 normal-case font-semibold">About {minutes(current.words)} min read</span>}
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold mt-1 mb-2 leading-tight" style={{ color: NAVY }}>{current?.title}</h2>

              {loadingCh && !cache[n] && <div className="py-16 flex justify-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}
              {chError && <p className="py-8 text-sm text-red-600">{chError}</p>}

              {lockReason && (
                <div className="my-8 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8 text-center">
                  <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center" style={{ background: NAVY }}>
                    <Lock className="w-5 h-5 text-teal-300" />
                  </div>
                  <h3 className="mt-4 text-lg font-extrabold" style={{ color: NAVY }}>This chapter is for subscribers</h3>
                  <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
                    {lockReason === 'signin'
                      ? 'Create a free account, then choose a plan to read the full guide, download it as a PDF and practise with timed mock exams.'
                      : 'Choose a plan to read the full guide, download it as a PDF and practise with timed mock exams.'}
                  </p>
                  <div className="mt-5 flex flex-wrap justify-center gap-2">
                    {lockReason === 'signin' ? (
                      <>
                        <button onClick={() => onOpenAuthModal?.('register')} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer" style={{ background: TEAL }}>Create free account</button>
                        <button onClick={() => onOpenAuthModal?.('login')} className="px-5 py-2.5 rounded-xl text-sm font-bold border border-slate-300 bg-white text-slate-700 cursor-pointer">Log in</button>
                      </>
                    ) : (
                      <button onClick={() => openPricingModal?.()} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white cursor-pointer" style={{ background: TEAL }}>See plans</button>
                    )}
                  </div>
                  <p className="mt-4 text-xs text-slate-500">Chapter 1 stays free to read and share.</p>
                </div>
              )}

              {cache[n] && <div className="gm-body"><GuideBlocks blocks={blocks} /></div>}

              <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                <button onClick={() => go(n - 1)} disabled={n <= 1} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 disabled:opacity-40 cursor-pointer">
                  <ArrowLeft className="w-4 h-4" /> Previous
                </button>
                {onStartTest && (
                  <button onClick={() => onStartTest(paperId)} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold cursor-pointer" style={{ color: TEAL }}>
                    <Play className="w-4 h-4" /> Practise this exam
                  </button>
                )}
                <button onClick={() => go(n + 1)} disabled={n >= total} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-40 cursor-pointer" style={{ background: NAVY }}>
                  Next <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </main>
        </div>
      </div>

      {tocOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setTocOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[86%] max-w-sm bg-white p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-extrabold" style={{ color: NAVY }}>Chapters</span>
              <button onClick={() => setTocOpen(false)} className="p-1 cursor-pointer"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            {toc}
          </div>
        </div>
      )}
    </div>
  );
};

const PrintDocument: React.FC<{ meta: GuideMeta; chapters: { meta: ChapterMeta; data: ChapterData | null }[] }> = ({ meta, chapters }) => {
  const year = new Date().getFullYear();
  return (
    <div>
      <style>{`@page{@bottom-center{content:"prepraxis.com   Page " counter(page);font:10px system-ui;color:#94a3b8}}`}</style>
      <div className="gp-hint">If the print window does not open, press Ctrl+P (or Cmd+P on a Mac) and choose "Save as PDF".</div>
      <section className="gp-cover">
        <div className="gp-band">
          <div className="gp-brand">prepraxis.com study guide</div>
          <div className="gp-title">{meta.title}</div>
          {meta.subtitle && <div className="gp-sub">{meta.subtitle}</div>}
        </div>
        <div className="gp-meta">
          <div><strong>{meta.edition || `${year} Edition`}</strong></div>
          <div>Copyright {year} prepraxis.com. All rights reserved. For the personal use of the subscriber who downloaded it.</div>
          <div>Praxis is a registered trademark of ETS. This guide is not endorsed by or affiliated with ETS or any state agency.</div>
        </div>
      </section>
      <section className="gp-toc">
        <h2>Contents</h2>
        <ol>{chapters.map((c) => <li key={c.meta.n}>{c.meta.title}</li>)}</ol>
      </section>
      {chapters.map((c) => (
        <section key={c.meta.n} className="gp-chapter">
          <div className="gp-chapter-head">
            <div className="gp-chapter-num">Chapter {c.meta.n}</div>
            <div className="gp-chapter-title">{c.meta.title}</div>
          </div>
          {c.data ? (
            <div className="gm-body"><GuideBlocks blocks={parseGuideMarkdown(c.data.markdown)} print /></div>
          ) : (
            <div className="gp-locked">This chapter could not be included. Open the guide on prepraxis.com to read it.</div>
          )}
        </section>
      ))}
    </div>
  );
};

export default WebGuideReader;
